<template>
  <div class="camp-page">
    <div class="camp-header">
      <div class="camp-header__text">
        <h1 class="camp-title">Find campaigns</h1>
        <p class="camp-subtitle">All available campaigns on the platform</p>
      </div>

      <div class="camp-header__actions">
        <span v-show="items.length > 0 && $vuetify.breakpoint.smAndUp" class="camp-view-toggle">
          <button
            class="camp-view-btn"
            :class="{ 'camp-view-btn--active': displayMode === 'card' }"
            title="Grid view"
            @click="changeListView('card')"
          >
            <i class="fal fa-table"></i>
          </button>
          <button
            class="camp-view-btn"
            :class="{ 'camp-view-btn--active': displayMode === 'table' }"
            title="Table view"
            @click="changeListView('table')"
          >
            <i class="fal fa-list"></i>
          </button>
        </span>

        <button
          v-if="$vuetify.breakpoint.smAndUp"
          class="camp-filter-btn"
          @click="filtersTmp = cloneDeep(filters); showFiltersDialog = true"
        >
          <i class="fas fa-filter"></i> Filter
          <span v-if="filtersCount > 0" class="camp-filter-badge">{{ filtersCount }}</span>
        </button>

        <!--Mobile buttons-->
        <v-btn
          color="accent"
          class="hidden-sm-and-up second-fixed-bottom-right"
          fixed
          bottom
          right
          fab
          @click="changeListView('table')"
          v-show="items.length > 0 && displayMode === 'card'"
        >
          <v-icon color="white">fal fa-list</v-icon>
        </v-btn>
        <v-btn
          color="accent"
          class="hidden-sm-and-up second-fixed-bottom-right"
          fixed
          bottom
          right
          fab
          @click="changeListView('card')"
          v-show="items.length > 0 && displayMode === 'table'"
        >
          <v-icon color="white">fal fa-table</v-icon>
        </v-btn>
        <v-btn
          color="accent"
          class="hidden-sm-and-up"
          fixed
          bottom
          right
          fab
          @click="showFiltersDialog = true"
        >
          <v-icon color="white">fas fa-filter</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-if="items.length === 0 && !loading" class="camp-empty">
      <p>There are no results for your search.</p>
      <img
        src="@/assets/searchFailedInfluencer.svg"
        alt="search Failed Influencer"
        class="camp-empty__img"
      />
    </div>

    <template v-else-if="items.length > 0 || !loading">
      <div v-if="displayMode === 'card'" class="camp-grid">
        <campaign-list-card
          v-for="item in items"
          :key="item.uuid"
          :campaign="item"
        >
          <template v-slot:footer>
            <router-link
              :to="{ name: 'campaign', params: { id: item.uuid } }"
              class="camp-btn camp-btn--primary"
            >
              See campaign
            </router-link>
          </template>
        </campaign-list-card>
      </div>

      <div v-else class="camp-table-card">
        <campaigns-data-table
          :isInfluencer="true"
          :items="items"
          :page="page"
          :total="total"
          :loading="loading"
          @changePage="loadCampaigns($event)"
        ></campaigns-data-table>
      </div>

      <div v-if="displayMode === 'card' && items.length" class="camp-pagination">
        <v-pagination
          v-model="page"
          :length="total"
          :total-visible="7"
          @input="handlePageChange"
        ></v-pagination>
      </div>
    </template>

    <div v-else class="camp-grid">
      <div v-for="(skeleton, i) of skeletonArray" :key="i" class="camp-skel-card">
        <div class="camp-skel-block camp-skel-img"></div>
        <div class="camp-skel-block camp-skel-line camp-skel-line--wide"></div>
        <div class="camp-skel-block camp-skel-line camp-skel-line--mid"></div>
        <div class="camp-skel-block camp-skel-line camp-skel-line--short"></div>
      </div>
    </div>

    <find-campaign-filters-dialog
      v-model="showFiltersDialog"
      :filters="filters"
      @save="saveFilters"
    ></find-campaign-filters-dialog>
  </div>
</template>
<script>
import search_filters_url from "@/helpers/mixins/search_filters_url";
import FindCampaignFiltersDialog from "../dialogs/FindCampaignFiltersDialog";
import CampaignListCard from "../../cards/CampaignListCard";
import local_numbers from "@/helpers/filters/local_numbers";
import friendly_numbers from "@/helpers/filters/friendly_numbers";
import utc_to_local_datetime from "@/helpers/filters/utc_to_local_datetime";
import CampaignsDataTable from "../../data-tables/CampaignsDataTable";
import { mapActions } from "vuex";

export default {
  mixins: [search_filters_url],
  components: {
    CampaignListCard,
    FindCampaignFiltersDialog,
    CampaignsDataTable
  },
  filters: { local_numbers, friendly_numbers, utc_to_local_datetime },
  data: () => ({
    filters: {},
    loading: false,
    total: null,
    page: 1,
    items: [],
    skeletonArray: [],
    filtersUpdateTimeout: null,
    showFiltersDialog: false,
    displayMode: "card",
    prevRouteName: null
  }),
  computed: {
    filtersCount() {
      let filters = this.cloneDeep(this.filters);
      let count = Object.keys(filters).length;
      // If search key is set but has no value. Then subtract 1 from count
      if ("search" in filters && !filters.search) {
        count--;
      } else if ("limit" in filters && "page" in filters) {
        return count - 2;
      }
      return count;
    },
    isCampaginRoute() {
      return this.prevRouteName === "campaign" || !this.prevRouteName;
    }
  },
  methods: {
    ...mapActions("core/campaigns", { getCampaigns: "loadCampaigns" }),
    handlePageChange(value) {
      this.scrollToTop();
      this.page = value;
      this.loadCampaigns(value);
    },
    saveFilters(event) {
      this.filters = event;
      this.page = 1;
      this.loadCampaigns(this.page);
    },
    loadCampaigns(page = 1) {
      this.loading = true;
      this.page = page;
      this.addSkeletons();
      let filters = this.filters;

      if (filters.category_ids) {
        filters.category_ids = filters.category_ids.map(id => {
          return Number(id);
        });
      }
      filters.limit = 18;
      filters.page = page;
      const params = {
        ...filters,
        active_campaigns: true,
        with: ["influencers"],
        order_by_recommended: true
      };
      this.setUrlSearchFilters(filters);

      this.getCampaigns(params).then(campaigns => {
        this.total = campaigns.lastPage;
        this.items = campaigns.response;
        this.loading = false;
      });
    },
    changeListView(mode) {
      this.displayMode = mode;
      this.$nextTick(() => {
        window.scrollTo({ top: 0 });
      });
    },
    addSkeletons() {
      let arr = [];
      for (let i = 0; i < 12; i++) {
        arr.push({ id: "skeleton" });
      }
      this.skeletonArray = arr;
    }
  },
  created() {
    if (Object.keys(this.$route.query).length > 0) {
      const filters = this.getUrlSearchFilters();
      this.filters = filters;
      this.page = filters.page;
    }
    this.loadCampaigns(this.page);
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRouteName = from.name;
    });
  }
};
</script>
<style lang="scss" scoped>
.camp-page {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 24px 20px 120px;
}

.camp-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.camp-title { font-size: 22px; font-weight: 650; color: var(--ink); margin: 0; letter-spacing: -.02em; }
.camp-subtitle { font-size: 13px; color: var(--ink-3, #888); margin: 4px 0 0; }

.camp-header__actions { display: flex; align-items: center; gap: 10px; }

.camp-view-toggle {
  display: flex;
  border: 1px solid var(--line, #e0e0e0);
  border-radius: var(--r-ctrl, 8px);
  overflow: hidden;
}
.camp-view-btn {
  width: 36px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface, #fff);
  border: none;
  color: var(--ink-3, #888);
  cursor: pointer;
  font-size: 13px;
}
.camp-view-btn + .camp-view-btn { border-left: 1px solid var(--line, #e0e0e0); }
.camp-view-btn--active { background: var(--surface-2, #f3f3f3); color: var(--accent, #e05540); }

.camp-filter-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: 7px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--line, #e0e0e0);
  border-radius: var(--r-ctrl, 8px);
  background: var(--surface, #fff);
  color: var(--ink-2, #444);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.camp-filter-btn:hover { background: var(--surface-2, #f7f7f7); }
.camp-filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--accent, #e05540);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.camp-empty {
  text-align: center;
  padding: 48px 0;
  color: var(--ink-3, #888);
  font-size: 13px;
}
.camp-empty__img { max-width: 260px; margin: 20px auto 0; display: block; }

.camp-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
}
@media (max-width: 1100px) { .camp-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .camp-grid { grid-template-columns: 1fr; } }

.camp-table-card {
  background: var(--surface, #fff);
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  overflow: hidden;
}

.camp-btn {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-ctrl, 8px);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
  width: 100%;
}
.camp-btn.camp-btn--primary {
  background: var(--accent, #e05540);
}
.camp-grid a.camp-btn.camp-btn--primary {
  color: #fff !important;
}
.camp-btn--primary:hover { opacity: .9; }

.camp-pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.camp-pagination ::v-deep .v-pagination__item--active {
  background: var(--accent, #e05540) !important;
  color: #fff !important;
}

/* Skeleton loading */
.camp-skel-card {
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  overflow: hidden;
  padding: 16px;
  background: var(--surface, #fff);
}
.camp-skel-block {
  background: linear-gradient(90deg, var(--surface-2, #f0f0f0) 25%, var(--line, #e5e5e5) 50%, var(--surface-2, #f0f0f0) 75%);
  background-size: 200% 100%;
  animation: camp-skel-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}
.camp-skel-img { height: 150px; margin: -16px -16px 14px; border-radius: 0; }
.camp-skel-line { height: 12px; margin-bottom: 8px; }
.camp-skel-line--wide { width: 90%; }
.camp-skel-line--mid { width: 65%; }
.camp-skel-line--short { width: 45%; margin-bottom: 0; }
@keyframes camp-skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media only screen and (max-width: 960px) {
  ::v-deep .virtual-scroll-item {
    width: 50% !important;
  }
}
@media only screen and (max-width: 600px) {
  ::v-deep .virtual-scroll-item {
    width: 100% !important;
  }
}
.second-fixed-bottom-right {
  bottom: 86px !important;
  padding: 0px !important;
}
</style>